import { api } from '../api';
import { invalidateMultiple, provider, tags } from '../tags';
import type {
  AccountBenefit,
  AzureBenefit,
  AzureTechnicalAdvisory,
  ClientEntryPointEnum,
  components,
  operations,
  RedeemedBenefit,
  UrlBenefit,
  VSLicense,
} from '../types';

type ResponseRedeemedBenefit = components['schemas']['RedeemedBenefit'];

/* istanbul ignore file */

type ArgsWithSlug = { slug: string };
type ArgsWithPageName = { page_name?: ClientEntryPointEnum };
type WithFile = { file: File | null };
type ActivateAzureBenefitArgs = operations['createAzureBenefit']['parameters']['path'] & ArgsWithPageName;
type ActivateUrlBenefitArgs = operations['urlBenefitUrlBenefit']['parameters']['path'] &
  ArgsWithSlug &
  ArgsWithPageName;
type RedeemedBenefitArgs = operations['listRedeemedBenefits']['parameters']['path'];
type ActivateATABenefitArgs = operations['createAzureTechnicalAdvisory']['parameters']['path'] &
  AzureTechnicalAdvisory &
  ArgsWithSlug &
  WithFile;
type VSLicenseArgs = operations['createVSLicense']['parameters']['path'] & VSLicense & ArgsWithSlug;
type DestroyVSLicenseArgs = operations['destroyVSLicense']['parameters']['path'] & ArgsWithSlug;

const benefitTags = [tags.benefit, tags.redeemedBenefit, tags.accountBenefit, tags.levelup];

export const benefitsApi = api.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    accountBenefits: builder.query<Array<AccountBenefit>, operations['listAccountBenefits']['parameters']['path']>({
      query: ({ account_pk }) => `api/account/${account_pk}/benefits/`,
      providesTags: (result: Array<AccountBenefit> | void) =>
        result
          ? [tags.redeemedBenefit, ...result.map((benefit) => ({ type: tags.accountBenefit, id: benefit.slug }))]
          : [],
    }),

    redeemedBenefits: builder.query<Array<RedeemedBenefit>, RedeemedBenefitArgs>({
      query: ({ account_pk }) => `api/account/${account_pk}/redeemed_benefits/`,
      providesTags: provider<ResponseRedeemedBenefit>(tags.redeemedBenefit, 'benefit_slug'),
    }),

    activateUrlBenefit: builder.mutation<UrlBenefit, ActivateUrlBenefitArgs>({
      query: ({ id, slug, page_name }) => ({
        url: `api/account/${id}/url_benefit/`,
        method: 'POST',
        body: { benefit_slug: slug, from_client_page: page_name },
      }),
      invalidatesTags: invalidateMultiple<UrlBenefit, ActivateUrlBenefitArgs>(benefitTags, undefined, 'slug'),
    }),

    activateAzureBenefit: builder.mutation<AzureBenefit, ActivateAzureBenefitArgs>({
      query: ({ account_pk, page_name }) => ({
        url: `api/account/${account_pk}/azure_benefit/`,
        method: 'POST',
        body: { from_client_page: page_name },
      }),
      invalidatesTags: invalidateMultiple<AzureBenefit, ActivateAzureBenefitArgs>(
        [...benefitTags, tags.airsConsumption],
        undefined,
        'slug'
      ),
    }),

    activateATABenefit: builder.mutation<AzureTechnicalAdvisory, ActivateATABenefitArgs>({
      query: ({ account_pk, slug, ...data }) => ({
        url: `api/account/${account_pk}/ata/`,
        method: 'POST',
        body: createFormData(data),
      }),
      invalidatesTags: invalidateMultiple<AzureTechnicalAdvisory, ActivateATABenefitArgs>(
        benefitTags,
        undefined,
        'slug'
      ),
    }),

    visualStudioBenefit: builder.query<Array<VSLicense>, operations['listVSLicenses']['parameters']['path']>({
      query: ({ account_pk }) => `api/account/${account_pk}/vs_benefit/`,
      providesTags: provider<VSLicense>(tags.accountBenefit, 'id'),
    }),

    activateVisualStudioBenefit: builder.mutation<VSLicense, VSLicenseArgs>({
      query: ({ account_pk, slug, ...data }) => ({
        url: `api/account/${account_pk}/vs_benefit/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [tags.benefit, tags.accountBenefit],
    }),

    destroyVisualStudioBenefit: builder.mutation<void, DestroyVSLicenseArgs>({
      query: ({ account_pk, id }) => ({ url: `api/account/${account_pk}/vs_benefit/${id}/`, method: 'DELETE' }),
      invalidatesTags: [tags.accountBenefit],
    }),
  }),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createFormData(args: any): FormData {
  const formData = new FormData();
  Object.keys(args).forEach((key) => {
    formData.append(key, args[key]);
  });
  return formData;
}
